import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useState, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import IllustrationConfetti from "@repo/icons/colored/IllustrationConfetti";
import ArrowRight from "@repo/icons/default/ArrowRight";
import InformationCircle from "@repo/icons/default/InformationCircle";

import { ScheduleListItem } from "@/components/schedule/list/schedule-list-item";
import { Button } from "@/components/ui/button/button";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import {
  EmptyStateV2Description,
  EmptyStateV2Root,
  EmptyStateV2Title,
} from "@/components/ui/empty-state/empty-state-v2";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useExitFirstSchedule } from "@/hooks/use-first-schedule-history";
import { useGraphQL } from "@/hooks/use-graphql";
import { firstScheduleAtom } from "@/lib/atoms/first-schedule-atom";
import { requireCreatedSchedules } from "@/lib/helpers/first-schedule-guards";

const FirstScheduleSuccessQuery = graphql(`
  query FirstScheduleSuccessQuery($codAgendas: [Int!]!) {
    usuariosAgendas(
      where: { codUsuarioAgenda: { _in: $codAgendas }, ativo: { _eq: true } }
      order_by: { codUsuarioAgenda: desc }
    ) {
      codUsuarioAgenda
      ...ScheduleListItemFragment
    }
  }
`);

const firstScheduleSuccessSearchSchema = z.object({
  appointmentTypes: z.enum(["saved", "skipped"]).optional(),
});

const getSuccessTitle = (
  totalSchedules: number,
  appointmentTypesSaved: boolean,
): string => {
  if (appointmentTypesSaved) {
    return totalSchedules === 1
      ? "Sua agenda está pronta para receber pacientes"
      : "Suas agendas estão prontas para receber pacientes";
  }

  return totalSchedules === 1
    ? "Sua primeira agenda foi criada!"
    : "Suas agendas foram criadas!";
};

const FirstScheduleSuccessPage = (): JSX.Element | null => {
  const navigate = useNavigate();
  const router = useRouter();
  const exitFirstSchedule = useExitFirstSchedule();
  const form = useAtomValue(firstScheduleAtom);
  const { appointmentTypes } = useSearch({ from: "/onboarding/first-schedule/success" });
  const [showNewScheduleDrawer, setShowNewScheduleDrawer] = useState(false);

  const { data } = useGraphQL(
    FirstScheduleSuccessQuery,
    { codAgendas: form.codAgendas },
    { enabled: form.codAgendas.length > 0 },
  );

  const handleGoHome = (): void => {
    exitFirstSchedule(() => {
      if (router.history.location.pathname !== "/home") {
        void navigate({ to: "/home", replace: true });
      }
    });
  };

  const handleEdit = (): void => {
    void navigate({
      to: "/onboarding/first-schedule",
      search: { codAgendas: form.codAgendas },
    });
  };

  const handleGoToSchedules = (): void => {
    exitFirstSchedule(() => {
      void navigate({
        to: "/settings/schedule-options/schedules",
        search: { from: "/home" },
      });
    });
  };

  const schedules = data?.usuariosAgendas ?? [];

  if (form.codAgendas.length === 0) return null;

  return (
    <RoundedLayout>
      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <EmptyStateV2Root className="gap-4 py-4">
          <IllustrationConfetti size={80} aria-hidden="true" />

          <div>
            <EmptyStateV2Title className="text-2xl font-medium">
              {getSuccessTitle(form.codAgendas.length, appointmentTypes === "saved")}
            </EmptyStateV2Title>
            <EmptyStateV2Description className="text-md">
              Você pode editar ou criar novas agendas a qualquer momento.
            </EmptyStateV2Description>
          </div>

          <div className="flex flex-col gap-3">
            {schedules.map((schedule) => (
              <ScheduleListItem
                key={schedule.codUsuarioAgenda}
                schedule={schedule}
                onEdit={handleEdit}
              />
            ))}
          </div>
        </EmptyStateV2Root>

        <div className="sticky bottom-0 flex flex-col gap-3 pt-4 pb-4 bg-white">
          <Button fullWidth color="secondary" onClick={handleGoHome}>
            <div className="flex flex-row items-center justify-center gap-2">
              Ir para home
              <ArrowRight size={24} aria-hidden="true" />
            </div>
          </Button>
          <Button
            fullWidth
            color="secondaryLight"
            onClick={() => setShowNewScheduleDrawer(true)}
          >
            Criar nova agenda
          </Button>
        </div>
      </Page>

      <MessageDrawerRoot
        icon={InformationCircle}
        open={showNewScheduleDrawer}
        setOpen={setShowNewScheduleDrawer}
        variant="secondary"
      >
        <MessageDrawerTitle>Quer atender em mais de uma unidade?</MessageDrawerTitle>
        <MessageDrawerBody>
          Acesse suas agendas e crie uma nova agenda para cada unidade que desejar
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={handleGoToSchedules}>
            Ir para minhas agendas
          </MessageDrawerActionButton>
          <MessageDrawerActionButton
            secondary
            onClick={() => setShowNewScheduleDrawer(false)}
          >
            Cancelar
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/success")({
  validateSearch: firstScheduleSuccessSearchSchema,
  beforeLoad: requireCreatedSchedules,
  component: FirstScheduleSuccessPage,
});
